import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { 
  Users, 
  Award, 
  BookOpen,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Clock,
  Code,
  Brain,
  Cpu,
  Briefcase
} from "lucide-react";
import { 
  Container, 
  Section, 
  Grid, 
  Card, 
  PrimaryButton, 
  SecondaryButton, 
  Badge
} from "../styles/GlobalStyles";

// Internship Section
const InternshipSection = styled(Section)`
  background: linear-gradient(135deg, var(--gray-50) 0%, var(--secondary-50) 50%, var(--primary-50) 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -30%;
    right: -30%;
    width: 60%;
    height: 60%;
    background: radial-gradient(circle, rgba(34, 197, 94, 0.1) 0%, transparent 70%);
    animation: float 15s ease-in-out infinite;
    pointer-events: none;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-16);
  
  @media (max-width: 768px) {
    margin-bottom: var(--space-12);
  }
  
  .badge {
    margin-bottom: var(--space-4);
  }
  
  h1 {
    font-size: var(--text-6xl);
    color: var(--gray-900);
    margin-bottom: var(--space-6);
    font-weight: 800;
    
    @media (max-width: 1024px) {
      font-size: var(--text-5xl);
    }
    
    @media (max-width: 768px) {
      font-size: var(--text-4xl);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-3xl);
    }
  }
  
  .subtitle {
    font-size: var(--text-xl);
    color: var(--gray-600);
    max-width: 700px;
    margin: 0 auto;
    line-height: var(--leading-relaxed);
    
    @media (max-width: 768px) {
      font-size: var(--text-lg);
      padding: 0 var(--space-4);
    }
  }
`;

const InternshipGrid = styled(Grid)`
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--space-8);
  margin-bottom: var(--space-16);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--space-6);
    margin-bottom: var(--space-12);
  }
`;

const InternshipCard = styled(Card)`
  padding: var(--space-8);
  border: 2px solid transparent;
  background: var(--gray-0);
  position: relative;
  transition: all var(--transition-normal);
  
  @media (max-width: 768px) {
    padding: var(--space-6);
  }
  
  &:hover {
    border-color: var(--secondary-200);
    transform: translateY(-8px);
    box-shadow: var(--shadow-2xl);
    
    @media (max-width: 768px) {
      transform: translateY(-4px);
    }
    
    .icon-container {
      background: var(--gradient-secondary);
      transform: scale(1.1) rotate(5deg);
      
      .icon {
        color: var(--gray-0);
      }
    }
  }
  
  .icon-container {
    width: 80px;
    height: 80px;
    margin: 0 auto var(--space-6);
    background: var(--secondary-50);
    border-radius: var(--radius-3xl);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all var(--transition-normal);
    
    @media (max-width: 768px) {
      width: 70px;
      height: 70px;
      margin-bottom: var(--space-4);
    }
    
    .icon {
      color: var(--secondary-600);
      transition: all var(--transition-normal);
    }
  }
  
  .card-header {
    text-align: center;
    margin-bottom: var(--space-6);
    
    h3 {
      font-size: var(--text-2xl);
      color: var(--gray-900);
      margin-bottom: var(--space-3);
      font-weight: 700;
      
      @media (max-width: 768px) {
        font-size: var(--text-xl);
      }
    }
    
    .duration {
      display: inline-flex;
      align-items: center;
      gap: var(--space-1);
      background: var(--secondary-100);
      color: var(--secondary-700);
      padding: var(--space-1) var(--space-3);
      border-radius: var(--radius-full);
      font-size: var(--text-sm);
      font-weight: 500;
    }
  }
  
  .description {
    color: var(--gray-600);
    line-height: var(--leading-relaxed);
    margin-bottom: var(--space-6);
    text-align: center;
    
    @media (max-width: 768px) {
      font-size: var(--text-sm);
    }
  }
  
  .features {
    margin-bottom: var(--space-6);
    
    h4 {
      font-size: var(--text-lg);
      color: var(--gray-900);
      margin-bottom: var(--space-4);
      font-weight: 600;
      
      @media (max-width: 768px) {
        font-size: var(--text-base);
      }
    }
    
    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      
      li {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) 0;
        color: var(--gray-600);
        font-size: var(--text-sm);
        
        .check-icon {
          color: var(--secondary-500);
          width: 16px;
          height: 16px;
        }
      }
    }
  }
  
  .stats {
    display: flex;
    justify-content: space-between;
    margin-bottom: var(--space-6);
    padding: var(--space-4);
    background: var(--gray-50);
    border-radius: var(--radius-lg);
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: var(--space-2);
      text-align: center;
    }
    
    .stat {
      text-align: center;
      
      .value {
        font-size: var(--text-xl);
        font-weight: 700;
        color: var(--secondary-600);
        display: block;
        
        @media (max-width: 768px) {
          font-size: var(--text-lg);
        }
      }
      
      .label {
        font-size: var(--text-xs);
        color: var(--gray-600);
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
    }
  }
`;

const BenefitsSection = styled.div`
  background: var(--gradient-secondary);
  border-radius: var(--radius-3xl);
  padding: var(--space-16) var(--space-8);
  color: var(--gray-0);
  text-align: center;
  margin-bottom: var(--space-16);
  
  @media (max-width: 768px) {
    padding: var(--space-12) var(--space-6);
    margin-bottom: var(--space-12);
  }
  
  h2 {
    font-size: var(--text-4xl);
    margin-bottom: var(--space-8);
    font-weight: 800;
    
    @media (max-width: 768px) {
      font-size: var(--text-3xl);
      margin-bottom: var(--space-6);
    }
  }
  
  .benefits-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: var(--space-6);
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: var(--space-4);
    }
  }
  
  .benefit {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-3);
    
    .benefit-icon {
      width: 60px;
      height: 60px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: var(--radius-full);
      display: flex;
      align-items: center;
      justify-content: center;
      
      @media (max-width: 768px) {
        width: 50px;
        height: 50px;
      }
    }
    
    h3 {
      font-size: var(--text-xl);
      font-weight: 600;
      
      @media (max-width: 768px) {
        font-size: var(--text-lg);
      }
    }
    
    p {
      opacity: 0.9;
      text-align: center;
      
      @media (max-width: 768px) {
        font-size: var(--text-sm);
      }
    }
  }
`;

const ApplicationProcess = styled.div`
  margin-bottom: var(--space-16);
  
  @media (max-width: 768px) {
    margin-bottom: var(--space-12);
  }
  
  h2 {
    text-align: center;
    font-size: var(--text-4xl);
    color: var(--gray-900);
    margin-bottom: var(--space-12);
    font-weight: 700;
    
    @media (max-width: 768px) {
      font-size: var(--text-3xl);
      margin-bottom: var(--space-8);
    }
  }
  
  .process-steps {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: var(--space-6);
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: var(--space-4);
    }
  }
  
  .step {
    text-align: center;
    position: relative;
    
    .step-number {
      width: 60px;
      height: 60px;
      background: var(--primary-500);
      color: var(--gray-0);
      border-radius: var(--radius-full);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: var(--text-xl);
      font-weight: 700;
      margin: 0 auto var(--space-4);
    }
    
    h3 {
      font-size: var(--text-xl);
      color: var(--gray-900);
      margin-bottom: var(--space-2);
      font-weight: 600;
      
      @media (max-width: 768px) {
        font-size: var(--text-lg);
      }
    }
    
    p {
      color: var(--gray-600);
      
      @media (max-width: 768px) {
        font-size: var(--text-sm);
      }
    }
  }
`;

const CtaSection = styled.div`
  text-align: center;
  background: var(--gradient-primary);
  border-radius: var(--radius-3xl);
  padding: var(--space-16) var(--space-8);
  color: var(--gray-0);
  
  @media (max-width: 768px) {
    padding: var(--space-12) var(--space-6);
  }
  
  h2 {
    font-size: var(--text-4xl);
    margin-bottom: var(--space-4);
    font-weight: 800;
    
    @media (max-width: 768px) {
      font-size: var(--text-3xl);
    }
  }
  
  p {
    font-size: var(--text-xl);
    margin-bottom: var(--space-8);
    opacity: 0.9;
    
    @media (max-width: 768px) {
      font-size: var(--text-lg);
      margin-bottom: var(--space-6);
    }
  }
  
  .cta-buttons {
    display: flex;
    gap: var(--space-4);
    justify-content: center;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: center;
      gap: var(--space-3);
    }
  }
`;

const WhiteButton = styled(SecondaryButton)`
  background: var(--gray-0);
  color: var(--primary-600);
  border: 2px solid var(--gray-0);
  
  &:hover {
    background: var(--primary-50);
    border-color: var(--gray-0);
  }
`;

// Internship Programs Data
const internshipPrograms = [
  {
    title: "Full-Stack Development Internship",
    duration: "3-6 months",
    icon: <Code size={32} />,
    description: "Gain hands-on experience building complete web applications with modern technologies and frameworks.",
    features: [
      "Real project experience",
      "Mentorship from industry experts",
      "Portfolio development",
      "Code review sessions",
      "Agile development practices",
      "Industry-standard tools"
    ],
    stats: {
      projects: "5+",
      mentorship: "1:1",
      placement: "85%"
    }
  },
  {
    title: "AI/ML Research Internship",
    duration: "4-6 months",
    icon: <Brain size={32} />,
    description: "Work on cutting-edge AI/ML projects and contribute to research in machine learning and artificial intelligence.",
    features: [
      "Research project participation",
      "Paper publication opportunities",
      "Advanced ML techniques",
      "Industry dataset access",
      "Conference presentation",
      "Patent filing assistance"
    ],
    stats: {
      projects: "3+",
      research: "2",
      placement: "90%"
    }
  },
  {
    title: "IoT Development Internship",
    duration: "3-5 months",
    icon: <Cpu size={32} />,
    description: "Build smart connected devices and IoT solutions for real-world applications and industrial use cases.",
    features: [
      "Hardware-software integration",
      "Sensor network design",
      "Cloud platform integration",
      "Mobile app development",
      "Industrial IoT exposure",
      "Smart system deployment"
    ],
    stats: {
      projects: "4+",
      devices: "10+",
      placement: "80%"
    }
  }
];

const benefits = [
  {
    icon: <Award size={24} />,
    title: "Industry Certification",
    description: "Receive certificates recognized by top tech companies"
  },
  {
    icon: <Users size={24} />,
    title: "Networking Opportunities",
    description: "Connect with professionals and build valuable relationships"
  },
  {
    icon: <TrendingUp size={24} />,
    title: "Skill Development",
    description: "Gain practical skills that employers are looking for"
  },
  {
    icon: <Briefcase size={24} />,
    title: "Job Placement Support",
    description: "85% of our interns receive job offers within 3 months"
  }
];

const applicationSteps = [
  {
    number: "1",
    title: "Apply Online",
    description: "Submit your application with resume and portfolio"
  },
  {
    number: "2",
    title: "Technical Assessment",
    description: "Complete our skills evaluation test"
  },
  {
    number: "3",
    title: "Interview",
    description: "Participate in technical and cultural fit interview"
  },
  {
    number: "4",
    title: "Start Internship",
    description: "Begin your journey with orientation and project assignment"
  }
];

const Internships = () => {
  const navigate = useNavigate();

  const handleApplyClick = (program) => {
    navigate('/contact', { state: { program: `${program} Internship` } });
  };

  return (
    <InternshipSection>
      <Container>
        <SectionHeader>
          <Badge className="badge">
            <Briefcase size={16} />
            Internship Programs
          </Badge>
          <h1>Launch Your Tech Career with Real-World Experience</h1>
          <p className="subtitle">
            Join our comprehensive internship programs and work on real projects while learning from industry experts
          </p>
        </SectionHeader>

        <InternshipGrid>
          {internshipPrograms.map((program, index) => (
            <InternshipCard key={index}>
              <div className="icon-container">
                <div className="icon">{program.icon}</div>
              </div>
              
              <div className="card-header">
                <h3>{program.title}</h3>
                <span className="duration">
                  <Clock size={14} />
                  {program.duration}
                </span>
              </div>

              <p className="description">{program.description}</p>

              <div className="stats">
                <div className="stat">
                  <span className="value">{program.stats.projects || program.stats.research || program.stats.devices}</span>
                  <span className="label">Projects</span>
                </div>
                <div className="stat">
                  <span className="value">{program.stats.mentorship || program.stats.research || program.stats.devices}</span>
                  <span className="label">Support</span>
                </div>
                <div className="stat">
                  <span className="value">{program.stats.placement}</span>
                  <span className="label">Placement</span>
                </div>
              </div>

              <div className="features">
                <h4>What You'll Get:</h4>
                <ul>
                  {program.features.map((feature, idx) => (
                    <li key={idx}>
                      <CheckCircle className="check-icon" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <PrimaryButton 
                onClick={() => handleApplyClick(program.title)}
                style={{ width: '100%' }}
              >
                Apply Now
                <ArrowRight size={16} />
              </PrimaryButton>
            </InternshipCard>
          ))}
        </InternshipGrid>

        <BenefitsSection>
          <h2>Why Choose Our Internship Programs?</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit">
                <div className="benefit-icon">
                  {benefit.icon}
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </BenefitsSection>

        <ApplicationProcess>
          <h2>Application Process</h2>
          <div className="process-steps">
            {applicationSteps.map((step, index) => (
              <div key={index} className="step">
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </ApplicationProcess>

        <CtaSection>
          <h2>Ready to Kickstart Your Career?</h2>
          <p>Join our internship programs and gain the experience you need to succeed in tech</p>
          <div className="cta-buttons">
            <WhiteButton onClick={() => navigate('/contact')}>
              <Users size={16} />
              Apply for Internship
            </WhiteButton>
            <WhiteButton onClick={() => navigate('/career-paths')}>
              <BookOpen size={16} />
              View Career Paths
            </WhiteButton>
          </div>
        </CtaSection>
      </Container>
    </InternshipSection>
  );
};

export default Internships;
